import { put, takeLatest, takeLeading, all } from "redux-saga/effects";

import { requestGraphQL } from "../../../js/utils/requests";
import { RECAPTCHA_VERSION } from "../../../packs/grecaptcha";
import { openNotificationAlert } from "../../utils/notificationsAlerts/actions";
import * as action from "../actions";
import { services, constants } from "../service";
import { ProjectMapper, AreasMapper, SoftwaresMapper, CoursesMapper } from "../mappers";
import {
  findErrorsInProject,
  handleErrorMessage,
  validateResponseAndThrowIfNeeded,
} from "../utils";

export function* getProjectRequest(config) {
  try {
    const response = yield requestGraphQL(services.getProject(config.id));

    validateResponseAndThrowIfNeeded(response);

    const data = response.data?.data?.projects?.edges;

    yield put(action.getProjectSuccess(ProjectMapper.hydrate(data[0].node)));
  } catch (error) {
    yield all([
      put(action.setProjectError(handleErrorMessage(error))),
      put(
        openNotificationAlert({
          type: "error",
        })
      ),
    ]);
  }
}

export function* getProjectWatcher() {
  yield takeLatest(constants.GET_PROJECT, getProjectRequest);
}

export function* getAssociableCoursesRequest(config) {
  try {
    const response = yield requestGraphQL(services.getAssociableCourses(config.id));

    validateResponseAndThrowIfNeeded(response);

    const data = response.data?.data?.associableCoursesToProject?.edges;

    // The endpoint is prepared to send more than one project node;
    // for now, we'll use the first element:
    yield put(action.getAssociableCoursesSuccess(CoursesMapper.hydrate(data)));
  } catch (error) {
    yield all([
      put(action.getAssociableCoursesError(handleErrorMessage(error))),
      put(
        openNotificationAlert({
          type: "error",
        })
      ),
    ]);
  }
}

export function* getAssociableCoursesWatcher() {
  yield takeLatest(constants.GET_ASSOCIABLE_COURSES, getAssociableCoursesRequest);
}

export function* getCourseRequest(config) {
  try {
    const response = yield requestGraphQL(services.getCourse(config.id));

    validateResponseAndThrowIfNeeded(response);

    const data = response.data?.data?.courses?.edges;

    yield put(action.getCourseSuccess(data[0].node));
  } catch (error) {
    yield all([
      put(action.getCourseError(handleErrorMessage(error))),
      put(
        openNotificationAlert({
          type: "error",
        })
      ),
    ]);
  }
}

export function* getCourseWatcher() {
  yield takeLatest(constants.GET_COURSE, getCourseRequest);
}

export function* getAreasRequest() {
  try {
    const response = yield requestGraphQL(services.getAreas());

    validateResponseAndThrowIfNeeded(response);

    const data = response.data?.data?.areas?.edges;

    // The endpoint is prepared to send more than one project node;
    // for now, we'll use the first element:
    yield put(action.getAreasSuccess(AreasMapper.hydrate(data)));
  } catch (error) {
    yield all([
      put(action.getAreasError(handleErrorMessage(error))),
      put(
        openNotificationAlert({
          type: "error",
        })
      ),
    ]);
  }
}

export function* getAreasWatcher() {
  yield takeLatest(constants.GET_AREAS, getAreasRequest);
}

export function* getSoftwaresRequest() {
  try {
    const response = yield requestGraphQL(services.getSoftwares());

    validateResponseAndThrowIfNeeded(response);

    const data = response.data?.data?.softwares?.edges;

    yield put(action.getSoftwaresSuccess(SoftwaresMapper.hydrate(data)));
  } catch (error) {
    yield all([
      put(action.getSoftwaresError(handleErrorMessage(error))),
      put(
        openNotificationAlert({
          type: "error",
        })
      ),
    ]);
  }
}

export function* getSoftwaresWatcher() {
  yield takeLatest(constants.GET_SOFTWARES, getSoftwaresRequest);
}

export function* saveProjectRequest(config) {
  try {
    const dataQuery = ProjectMapper.dehydrate(config.data);
    const response = yield requestGraphQL(
      services.saveProject(dataQuery, config.isPublic, config.reCaptchaToken)
    );

    validateResponseAndThrowIfNeeded(response);

    const error = response.data.data?.saveProject?.error;

    // Recaptcha validation error
    if (error && error.code === 4002) {
      yield all([
        put(action.setProjectError({ reCaptchaError: error.description })),
        put(
          openNotificationAlert({
            type: "error",
            message: "registrations.recaptcha.errors.verification_failed", // TODO should I move this copy to a shared namespace?
          })
        ),
        put(action.setReCaptchaVersion(RECAPTCHA_VERSION.V2)),
      ]);

      return;
    }

    if (response.data.errors) {
      yield all([
        put(action.setProjectError({ serverError: response.data.errors })),
        put(
          openNotificationAlert({
            type: "error",
          })
        ),
      ]);

      return;
    }

    const data = response.data.data?.saveProject?.project;
    const errors = findErrorsInProject(data);

    if (Object.keys(errors).length > 0) {
      yield all([
        put(action.setProjectError(errors)),
        put(
          openNotificationAlert({
            type: "error",
          })
        ),
      ]);

      return;
    }

    if (config.isPublic) {
      const url = data.railsRoutes?.projectUrl;
      const successQueryParam = "success_and_share=1";

      window.location.href = `${url}?${successQueryParam}`;

      return;
    }

    yield all([
      put(action.setProjectSuccess(ProjectMapper.hydrate(data))),
      put(action.setReCaptchaVersion(RECAPTCHA_VERSION.V3)),
      put(
        openNotificationAlert({
          type: "success",
        })
      ),
    ]);

    // Change path when project is saved for the first time
    if (window.location.pathname.includes("new") && data?.id) {
      const state = {};
      const title = "";
      const url = `${data.id}-${data.name}/edit`;

      history.pushState(state, title, url);
    }
  } catch (error) {
    yield all([
      put(action.setProjectError(handleErrorMessage(error))),
      put(
        openNotificationAlert({
          type: "error",
        })
      ),
    ]);
  }
}

export function* saveProjectWatcher() {
  yield takeLeading(constants.SET_PROJECT, saveProjectRequest);
}

export function* destroyProjectRequest(config) {
  try {
    const response = yield requestGraphQL(services.destroyProject(config.id));

    validateResponseAndThrowIfNeeded(response);

    if (response.data.errors) {
      yield all([
        put(action.deleteProjectError({ serverError: response.data.errors })),
        put(
          openNotificationAlert({
            type: "error",
          })
        ),
      ]);

      return;
    }

    const data = response.data?.data?.destroyProject?.project;

    yield all([
      put(action.deleteProjectSuccess()),
      put(
        openNotificationAlert({
          type: "success",
        })
      ),
    ]);

    const url = data.user?.railsRoutes?.portfolioUserUrl;

    window.location.href = url;
  } catch (error) {
    yield all([
      put(action.deleteProjectError(handleErrorMessage(error))),
      put(
        openNotificationAlert({
          type: "error",
        })
      ),
    ]);
  }
}
export function* destroyProjectWatcher() {
  yield takeLatest(constants.DELETE_PROJECT, destroyProjectRequest);
}

export default function* defaultSaga() {
  yield all([
    getProjectWatcher(),
    getAreasWatcher(),
    getAssociableCoursesWatcher(),
    getCourseWatcher(),
    getSoftwaresWatcher(),
    saveProjectWatcher(),
    destroyProjectWatcher(),
  ]);
}
